import * as React from 'react';
import { toast } from 'sonner';
import { Plus, CalendarClock } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { useApi } from '../../../design-system/useApi';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { hrApi } from '../api';
import type { ShiftTemplate, ShiftTemplateInput } from '../types';

const WEEKDAYS = [
  { key: 'mon', label: 'Mon' }, { key: 'tue', label: 'Tue' }, { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' }, { key: 'fri', label: 'Fri' }, { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
] as const;

type DayKey = typeof WEEKDAYS[number]['key'];
type DayDraft = Record<DayKey, { start: string; end: string }>; // start === '' means day off

function emptyDays(): DayDraft {
  const d = {} as DayDraft;
  for (const w of WEEKDAYS) d[w.key] = { start: '09:00', end: '17:00' };
  return d;
}

function draftFromTemplate(t: ShiftTemplate): DayDraft {
  const d = {} as DayDraft;
  for (const w of WEEKDAYS) {
    const s = t[`${w.key}_start` as keyof ShiftTemplate] as string | null;
    const e = t[`${w.key}_end` as keyof ShiftTemplate] as string | null;
    d[w.key] = { start: s ? s.slice(0, 5) : '', end: e ? e.slice(0, 5) : '' };
  }
  return d;
}

function toInput(name: string, days: DayDraft, isActive: boolean): ShiftTemplateInput {
  const out: Record<string, unknown> = { name, is_active: isActive };
  for (const w of WEEKDAYS) {
    const cell = days[w.key];
    out[`${w.key}_start`] = cell.start || null;
    out[`${w.key}_end`] = cell.end || null;
  }
  return out as unknown as ShiftTemplateInput;
}

function summarizeTemplate(t: ShiftTemplate): string {
  const parts: string[] = [];
  for (const w of WEEKDAYS) {
    const s = t[`${w.key}_start` as keyof ShiftTemplate] as string | null;
    const e = t[`${w.key}_end` as keyof ShiftTemplate] as string | null;
    if (s && e) parts.push(`${w.label} ${String(s).slice(0, 5)}–${String(e).slice(0, 5)}`);
  }
  return parts.length ? parts.join(', ') : 'No working days';
}

/**
 * HR management of reusable weekly shift templates — a Mon–Sun grid of
 * start/end time pairs (or "Off" for a non-working day). Self-contained,
 * no props — same pattern as LeaveTypesPanel. Callers embed it wherever a
 * templates manager belongs (SchedulePage hosts it inside a Dialog).
 */
export function ShiftTemplatesPanel() {
  const confirm = useConfirm();
  const { data, loading, reload } = useApi<{ templates: ShiftTemplate[] }>('/api/hr/schedule/templates');

  const [name, setName] = React.useState('');
  const [days, setDays] = React.useState<DayDraft>(emptyDays());
  const [isActive, setIsActive] = React.useState(true);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  function startEdit(t: ShiftTemplate) {
    setEditingId(t.id);
    setName(t.name);
    setDays(draftFromTemplate(t));
    setIsActive(t.is_active);
  }

  function cancelEdit() {
    setEditingId(null);
    setName('');
    setDays(emptyDays());
    setIsActive(true);
  }

  function setDayField(key: DayKey, field: 'start' | 'end', value: string) {
    setDays((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));
  }

  function toggleDayOff(key: DayKey) {
    setDays((d) => ({
      ...d,
      [key]: d[key].start ? { start: '', end: '' } : { start: '09:00', end: '17:00' },
    }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const input = toInput(name.trim(), days, isActive);
      if (editingId) {
        await hrApi.updateShiftTemplate(editingId, input);
        toast.success('Template updated');
      } else {
        await hrApi.createShiftTemplate(input);
        toast.success('Template created');
      }
      cancelEdit();
      reload();
    } catch (err: any) {
      toast.error(editingId ? 'Could not update' : 'Could not create', { description: err.message });
    } finally { setSaving(false); }
  }

  async function toggleActive(t: ShiftTemplate) {
    try {
      await hrApi.updateShiftTemplate(t.id, { is_active: !t.is_active });
      reload();
    } catch (err: any) { toast.error('Could not update', { description: err.message }); }
  }

  async function del(t: ShiftTemplate) {
    if (!(await confirm({
      title: 'Delete this template?',
      description: 'Templates still referenced by an assignment are archived instead of removed.',
      destructive: true,
      confirmLabel: 'Delete',
    }))) return;
    try {
      const { soft } = await hrApi.deleteShiftTemplate(t.id);
      toast.success(soft ? 'Archived — still in use by an assignment' : 'Deleted');
      if (t.id === editingId) cancelEdit();
      reload();
    } catch (err: any) { toast.error('Delete failed', { description: err.message }); }
  }

  return (
    <div className="space-y-6">
      <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
        <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>
          {editingId ? 'Edit template' : 'New template'}
        </h3>
        <form onSubmit={submit} className="grid gap-4">
          <div className="grid gap-1.5 max-w-sm">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Weekday 9–5" />
          </div>

          <div className="overflow-x-auto">
            <table className="text-[12.5px]">
              <thead>
                <tr>
                  {WEEKDAYS.map((w) => (
                    <th key={w.key} className="text-left text-[11px] uppercase tracking-[0.08em] font-medium text-muted-foreground pb-2 pr-3">
                      {w.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {WEEKDAYS.map((w) => {
                    const cell = days[w.key];
                    const off = !cell.start;
                    return (
                      <td key={w.key} className="pr-3 pb-2 align-top">
                        <button
                          type="button"
                          onClick={() => toggleDayOff(w.key)}
                          className={[
                            'mb-1.5 inline-flex items-center px-2 h-6 text-[10.5px] font-semibold rounded-full ring-1',
                            off
                              ? 'bg-muted/50 text-muted-foreground ring-border/40'
                              : 'bg-lime-neon/15 text-ink ring-lime-neon/30 dark:bg-lime-neon/10 dark:text-lime-neon dark:ring-lime-neon/25',
                          ].join(' ')}
                        >
                          {off ? 'Off' : 'Working'}
                        </button>
                        {!off && (
                          <div className="grid gap-1">
                            <Input
                              type="time"
                              aria-label={`${w.label} start`}
                              value={cell.start}
                              onChange={(e) => setDayField(w.key, 'start', e.target.value)}
                              className="h-8 text-[12px] w-[110px]"
                            />
                            <Input
                              type="time"
                              aria-label={`${w.label} end`}
                              value={cell.end}
                              onChange={(e) => setDayField(w.key, 'end', e.target.value)}
                              className="h-8 text-[12px] w-[110px]"
                            />
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer text-[13px] text-muted-foreground">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-saffron"
            />
            Active
          </label>

          <div className="flex justify-end gap-2 pt-1">
            {editingId && (
              <Button type="button" variant="ghost" disabled={saving} onClick={cancelEdit} className="rounded-full h-10 px-4 text-[13px] font-semibold text-muted-foreground">
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={saving} className="rounded-full h-10 px-5 text-[13px] font-semibold gap-1.5">
              {!editingId && <Plus className="h-3.5 w-3.5" />}
              {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create template'}
            </Button>
          </div>
        </form>
      </section>

      <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
        <DataTable<ShiftTemplate>
          loading={loading}
          rows={data?.templates ?? []}
          rowKey={(r) => r.id}
          columns={[
            { key: 'name', header: 'Name', cell: (r) => <span className="font-medium text-foreground">{r.name}</span> },
            { key: 'days', header: 'Days', cell: (r) => <span className="text-muted-foreground">{summarizeTemplate(r)}</span> },
            {
              key: 'active', header: 'Active', cell: (r) => r.is_active
                ? <span className={statusPill('success')}><span className={statusDot('success')} />Active</span>
                : <span className={statusPill('muted')}><span className={statusDot('muted')} />Inactive</span>,
            },
            {
              key: 'actions', header: '', cell: (r) => (
                <div className="flex gap-1 justify-end">
                  <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => startEdit(r)}>Edit</Button>
                  <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => toggleActive(r)}>{r.is_active ? 'Disable' : 'Enable'}</Button>
                  <Button size="sm" variant="ghost" className="rounded-full h-8 text-muted-foreground hover:text-destructive" onClick={() => del(r)}>Delete</Button>
                </div>
              ),
            },
          ]}
          emptyState={<EmptyState icon={<CalendarClock />} title="No shift templates" description="Create one above, then assign it to employees from the schedule grid." />}
        />
      </section>
    </div>
  );
}
